import type { OnChangeFn, SortingState } from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';

export interface UseTableStateOptions {
  tableId: string;
  defaultPageSize?: number;
  defaultSorting?: SortingState;
}

const DEFAULT_PAGE_SIZE = 25;

function readSearchParams() {
  return new URLSearchParams(window.location.search);
}

function writeSearchParams(next: URLSearchParams) {
  const qs = next.toString();
  const nextUrl = `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`;
  window.history.pushState(null, '', nextUrl);
}

function parseIntParam(v: string | null, fallback: number) {
  if (!v) return fallback;
  const n = Number(v);
  if (!Number.isFinite(n)) return fallback;
  const i = Math.floor(n);
  return i >= 0 ? i : fallback;
}

function parseSorting(params: URLSearchParams, defaultSorting: SortingState | undefined) {
  const sortBy = params.get('sort_by');
  const sortDir = params.get('sort_dir');
  if (!sortBy) return defaultSorting ?? [];
  const desc = sortDir === 'desc';
  return [{ id: sortBy, desc }];
}

function updateSortingParams(params: URLSearchParams, nextSorting: SortingState) {
  const first = nextSorting[0];
  if (!first) {
    params.delete('sort_by');
    params.delete('sort_dir');
    return;
  }
  params.set('sort_by', first.id);
  params.set('sort_dir', first.desc ? 'desc' : 'asc');
}

export function useTableState({ tableId, defaultPageSize, defaultSorting }: UseTableStateOptions) {
  void tableId; // reserved for future table-specific URL param scoping

  const initial = useMemo(() => {
    const params = readSearchParams();
    const pageSizeValue = parseIntParam(
      params.get('page_size'),
      defaultPageSize ?? DEFAULT_PAGE_SIZE,
    );
    const pageIndexValue = parseIntParam(params.get('page'), 0);
    const sortingValue = parseSorting(params, defaultSorting);
    return {
      pageIndexValue,
      pageSizeValue,
      sortingValue,
    };
  }, [defaultPageSize, defaultSorting]);

  const [sorting, setSortingState] = useState<SortingState>(initial.sortingValue);
  const [pageIndex, setPageIndexState] = useState(initial.pageIndexValue);
  const [pageSize, setPageSizeState] = useState(initial.pageSizeValue);

  const setSorting: OnChangeFn<SortingState> = useCallback(
    (updater) => {
      setSortingState((prev) => {
        const nextSorting = typeof updater === 'function' ? updater(prev) : updater;
        setPageIndexState(0);

        const params = readSearchParams();
        updateSortingParams(params, nextSorting);
        params.set('page', '0');
        params.set('page_size', String(pageSize));
        writeSearchParams(params);

        return nextSorting;
      });
    },
    [pageSize],
  );

  const setPageIndex = useCallback(
    (page: number) => {
      const nextPage = Math.max(0, Math.floor(page));
      setPageIndexState(nextPage);
      const params = readSearchParams();
      params.set('page', String(nextPage));
      params.set('page_size', String(pageSize));
      updateSortingParams(params, sorting);
      writeSearchParams(params);
    },
    [pageSize, sorting],
  );

  const setPageSize = useCallback(
    (size: number) => {
      const nextSize = Math.max(1, Math.floor(size));
      setPageSizeState(nextSize);
      setPageIndexState(0);

      const params = readSearchParams();
      params.set('page', '0');
      params.set('page_size', String(nextSize));
      updateSortingParams(params, sorting);
      writeSearchParams(params);
    },
    [sorting],
  );

  const resetPagination = useCallback(() => {
    setPageIndex(0);
  }, [setPageIndex]);

  useEffect(() => {
    const onPopState = () => {
      const params = readSearchParams();
      setPageIndexState(parseIntParam(params.get('page'), 0));
      setPageSizeState(
        parseIntParam(params.get('page_size'), defaultPageSize ?? DEFAULT_PAGE_SIZE),
      );
      setSortingState(parseSorting(params, defaultSorting));
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [defaultPageSize, defaultSorting]);

  return {
    sorting,
    setSorting,
    pageIndex,
    pageSize,
    setPageIndex,
    setPageSize,
    resetPagination,
  };
}
